import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Heart, ShoppingCart, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../constants";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";

const WishlistPage = () => {
  const { user, token } = useAuth();
  const { addToCart } = useCart();
  const { refresh } = useWishlist();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      if (!user) { setLoading(false); return; }
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const r = await axios.get(`${API}/wishlist`, { headers });
        setItems(r.data.items || []);
      } catch {
        toast.error("Failed to load wishlist");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [token, user]);

  const handleRemove = async (productId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/wishlist/${productId}`, { headers });
      setItems((prev) => prev.filter((p) => p.product_id !== productId));
      refresh();
      toast.success("Removed from wishlist");
    } catch {
      toast.error("Couldn't remove");
    }
  };

  const handleMoveToCart = async (product) => {
    await addToCart(product.product_id, 1);
    await handleRemove(product.product_id);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#050505] pt-24 px-4">
        <div className="max-w-2xl mx-auto py-16 text-center">
          <Heart className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 mb-4">Log in to use your wishlist.</p>
          <Link to="/login" className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-6 py-3 rounded-sm font-bold uppercase tracking-wider">
            Log In
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] pt-24 text-center py-32">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D62828] mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-16" data-testid="wishlist-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-4xl tracking-wide">YOUR WISHLIST</h1>
            <p className="text-gray-500 text-sm mt-1">{items.length} item{items.length === 1 ? '' : 's'} saved</p>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="text-center py-20">
            <Heart className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <p className="text-gray-400 mb-6">Your wishlist is empty.</p>
            <Link to="/products" className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-6 py-3 rounded-sm font-bold uppercase tracking-wider text-sm">
              Browse Products
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="wishlist-items">
            {items.map((p) => (
              <div key={p.product_id} className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden flex flex-col" data-testid={`wishlist-${p.product_id}`}>
                <Link to={`/products/${p.product_id}`} className="aspect-square bg-[#0A0A0A] overflow-hidden group">
                  <img
                    src={p.image_url || "https://images.unsplash.com/photo-1692486407015-3a47e4c1181c"}
                    alt={p.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  />
                </Link>
                <div className="p-4 flex-1 flex flex-col">
                  <Link to={`/products/${p.product_id}`} className="hover:text-[#D62828]">
                    <p className="text-white text-sm leading-tight font-medium line-clamp-2 mb-2">{p.name}</p>
                  </Link>
                  <p className="text-[#D62828] font-bold text-lg mb-3">${p.online_price?.toFixed(2)}</p>
                  <div className="mt-auto flex gap-2">
                    <button
                      onClick={() => handleMoveToCart(p)}
                      className="flex-1 bg-[#D62828] hover:bg-[#B91C1C] text-white px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-wide flex items-center justify-center gap-1.5"
                      data-testid={`wishlist-add-to-cart-${p.product_id}`}
                    >
                      <ShoppingCart className="w-3.5 h-3.5" /> Add
                    </button>
                    <button
                      onClick={() => handleRemove(p.product_id)}
                      title="Remove from wishlist"
                      className="px-3 py-2 border border-white/10 text-gray-400 hover:text-red-400 hover:border-red-500/50 rounded-sm transition-colors"
                      data-testid={`wishlist-remove-${p.product_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WishlistPage;
